export const DEBRIDEMENT_CONFIG_VERSION = "1.0.0";

/* ---- care settings ----
 * Keys match CUSTOMER_GROUP_NAME
 */
export const CARE_SETTINGS = {
  hospital: "Hospital",
  criticalaccess: "Critical access",
  rehabltac: "Rehab hospital/ LTAC",
  nursinghome: "Nursing Home",
  homehealthagency: "Home health agency",
  hospice: "Hospice",
  paceprogram: "Pace Program",
  woundcare: "Wound Care Center",
  podiatry: "Podiatry",
};

export const DEFAULT_CARE_SETTING = "hospital";

export const getCareSetting = (detailViewData) => {
  const group = detailViewData?.customerGroupCode;
  return group && CARE_SETTINGS[group] ? group : DEFAULT_CARE_SETTING;
};

/* ── prerequisite policy ──────────────────────────────────────────────────
 * R  = Required                — a "no" flags the record (High)
 * Rx = RequiredWithOverride    — a documented reason satisfies it (Medium)
 * O  = Optional                — shown, not enforced
 * null = Hidden                — not rendered; its absence is NOT a gap
 *
 * UNVALIDATED. Structural proposal pending clinical/compliance sign-off.
 * ------------------------------------------------------------------------ */
const P = { R: "R", Rx: "Rx", O: "O" };

export const PREREQUISITES = [
  {
    key: "consent",
    label: "Was verbal explanation of procedure given and consent received?",
    control: "tristate",
    policy: {
      hospital: P.R,
      criticalaccess: P.R,
      rehabltac: P.R,
      nursinghome: P.R,
      homehealthagency: P.R,
      hospice: P.R,
      paceprogram: P.R,
      woundcare: P.R,
      podiatry: P.R,
    },
    settingNotes: {
      nursinghome: "Care plan consent plus verbal; responsible party / POA path applies.",
      homehealthagency: "Consent-to-treat at admission plus verbal at visit.",
    },
    reasons: [
      { key: "onFileNotReobtained", value: "Consent on file, verbal not re-obtained" },
      { key: "emergent", value: "Emergent procedure" },
      { key: "notDocumented", value: "Not documented at time of care" },
      { key: "declinedToSign", value: "Patient declined to sign" },
      { key: "surrogateUnavailable", value: "Patient unable to consent — surrogate unavailable" },
    ],
  },
  {
    key: "performedBy",
    label: "Debridement performed by",
    control: "select",
    policy: {
      hospital: P.R,
      criticalaccess: P.R,
      rehabltac: P.R,
      nursinghome: P.R,
      homehealthagency: P.R,
      hospice: P.R,
      paceprogram: P.R,
      woundcare: P.R,
      podiatry: P.R,
    },
    settingNotes: {},
    options: [
      { key: "mddodpmanppaproviderresident", value: "MD, DO, DPM, ANP, PA, Provider Resident" },
      { key: "pt", value: "PT" },
      { key: "ptorrn-certified(cwswccwocn)", value: "PT or RN - Certified (CWS, WCC, WOCN, OMS)" },
    ],
    reasons: [],
  },
  {
    key: "providerOrder",
    label: "Is there a provider order?",
    control: "tristate",
    policy: {
      hospital: P.R,
      criticalaccess: P.Rx,
      rehabltac: P.Rx,
      nursinghome: P.Rx,
      homehealthagency: P.R,
      hospice: P.R,
      paceprogram: P.R,
      woundcare: P.Rx,
      podiatry: null,
    },
    settingNotes: {
      homehealthagency: "Must sit on the physician-established plan of care.",
      hospice: "Per plan of care.",
    },
    reasons: [
      { key: "emergentOrderToFollow", value: "Emergent — order to follow" },
      { key: "pendingProviderReview", value: "Order pending provider review" },
      { key: "standingProtocol", value: "Standing protocol" },
      { key: "providerUnreachable", value: "Unable to reach provider" },
      { key: "verbalPendingCountersign", value: "Verbal order — countersign pending" },
    ],
  },
  {
    key: "painBefore",
    label: "Pain scale obtained right before procedure",
    control: "pain",
    policy: {
      hospital: P.R,
      criticalaccess: P.Rx,
      rehabltac: P.Rx,
      nursinghome: P.Rx,
      homehealthagency: P.Rx,
      hospice: P.R,
      paceprogram: P.Rx,
      woundcare: P.Rx,
      podiatry: P.Rx,
    },
    settingNotes: {
      hospice: "Comfort is the primary endpoint in hospice, not a side measure.",
    },
    reasons: [
      { key: "nonverbal", value: "Nonverbal / cognitively impaired" },
      { key: "notObtainedEmergent", value: "Not obtained — emergent" },
      { key: "patientDeclined", value: "Patient declined" },
      { key: "unableToSelfReport", value: "Patient unable to self-report" },
    ],
  },
  {
    key: "photoPre",
    label: "Was a pre-debridement photo obtained?",
    control: "photo",
    policy: {
      hospital: P.R,
      criticalaccess: P.Rx,
      rehabltac: P.Rx,
      nursinghome: P.Rx,
      homehealthagency: P.O,
      hospice: P.O,
      paceprogram: P.O,
      woundcare: P.Rx,
      podiatry: P.Rx,
    },
    settingNotes: {
      homehealthagency: "Visit cadence cannot meet a fixed 4-day window — recency is visit-relative here.",
      hospice: "Visit-relative recency.",
      paceprogram: "Visit-relative recency.",
    },
    options: [
      { key: "rightBefore", value: "Right before procedure" },
      { key: "withinWindow", value: "Within recency window" },
    ],
    reasons: [
      { key: "policyProhibits", value: "Facility / home policy prohibits" },
      { key: "noCompliantDevice", value: "No compliant capture device" },
      { key: "patientDeclinedPhotography", value: "Patient declined photography" },
      { key: "notVisualisable", value: "Wound not visualisable" },
    ],
  },
];

export const PHOTO_RECENCY_WINDOW = {
  hospital: "48 hours",
  criticalaccess: "48 hours",
  rehabltac: "7 days",
  nursinghome: "7 days",
  homehealthagency: "this visit or prior visit",
  hospice: "this visit or prior visit",
  paceprogram: "this visit or prior visit",
  woundcare: "7 days",
  podiatry: "7 days",
};

/* ── Block 0 · router ─────────────────────────────────────────────────── */

export const NOT_PERFORMED_REASONS = [
  { key: "deferred", value: "Deferred to next visit" },
  { key: "noOrder", value: "No provider order available" },
  { key: "notIndicated", value: "Not clinically indicated" },
  { key: "notTolerated", value: "Not tolerated" },
  { key: "patientRepresentativeDeclined", value: "Patient / representative declined" },
];

/* ── Block 2 · procedure — V1 lists, verbatim ─────────────────────────── */

export const DEBRIDEMENT_TYPES = [
  {
    key: "awcm-extraction",
    value: "Active Wound Care Management (Open Approach) — Extraction / Pulling / Stripping",
    approachLabel: null,
    approaches: [],
  },
  {
    key: "awcm-nonselective",
    value: "Active Wound Care Management (Open Approach) — Non-selective, No Sharps",
    approachLabel: "Approach",
    approaches: [
      { key: "abrasion", value: "Abrasion" },
      { key: "autolytic", value: "Autolytic - Moist Wound Healing" },
      { key: "chemical", value: "Chemical - Silver Nitrate/Dakins/ Hydrogen Peroxide" },
      { key: "enzymatic", value: "Enzymatic" },
      { key: "mechanical", value: "Mechanical - Scrubbing/Wet to Dry" },
      { key: "wettodry", value: "Wet-to-Dry" },
    ],
    clinicalReviewNote:
      "V1 files Enzymatic under Non-selective. Enzymatic debridement is selective. Carried over as written — pending clinical review.",
  },
  {
    key: "awcm-selectivesharp",
    value: "Active Wound Care Management (Open Approach) — Selective Sharp",
    approachLabel: "Approach",
    approaches: [
      { key: "cutaway", value: "Cut Away" },
      { key: "minorpick", value: "Minor Pick" },
      { key: "minorscrape", value: "Minor Scrape" },
      { key: "minorsnip", value: "Minor Snip" },
    ],
  },
  {
    key: "surgical",
    value: "Surgical (Open Approach)",
    approachLabel: "Approach",
    approaches: [{ key: "cutaway", value: "Cut Away" }],
  },
];

export const DEPTH_OF_DEBRIDEMENT = [
  { key: "bone", value: "Bone" },
  { key: "cartilage", value: "Cartilage" },
  { key: "fascia", value: "Fascia" },
  { key: "muscle", value: "Muscle" },
  { key: "partialthickness", value: "Partial Thickness" },
  { key: "skin", value: "Skin" },
  { key: "subcutaneoustissue", value: "Subcutaneous Tissue", hasSubList: true },
  { key: "tendon", value: "Tendon" },
];

export const NECROTIC_SUBCUTANEOUS_TISSUE = [
  { key: "eschar", value: "Eschar" },
  { key: "fat", value: "Fat" },
  { key: "other", value: "Other" },
  { key: "sloughtissuetype", value: "Slough Tissue Type" },
];

export const NON_TISSUE_REMOVED = [
  { key: "biofilm", value: "Biofilm" },
  { key: "debris", value: "Debris" },
  { key: "mucosa", value: "Mucosa" },
  { key: "nails", value: "Nails" },
  { key: "skingraftorbiologiccovering", value: "Skin Graft or Biologic Covering" },
  { key: "yellowslough-nontissuetype", value: "Yellow Slough – non-Tissue Type" },
];

export const INSTRUMENTS_USED = [
  { key: "curette", value: "Curette" },
  { key: "cuttingflutetool", value: "Cutting Flute Tool" },
  { key: "forceps", value: "Forceps" },
  { key: "gauze", value: "Gauze" },
  { key: "highpressurewaterjet", value: "High Pressure Water Jet" },
  { key: "hydromechanicalvacveraflo", value: "Hydromechanical V.A.C. VeraFlo" },
  { key: "nailclipper", value: "Nail Clipper" },
  { key: "norsendebrider", value: "Norsen Debrider" },
  { key: "other", value: "Other" },
  { key: "scalpel", value: "Scalpel" },
  { key: "scissors", value: "Scissors" },
];

export const TECHNIQUE_USED = [
  { key: "bedsideclean", value: "Bedside Clean" },
  { key: "bedsidesterile", value: "Bedside Sterile" },
  { key: "orsterile", value: "OR Sterile" },
];

/* ── Block 3 · outcome ────────────────────────────────────────────────── */

export const BLEEDING_AMOUNT = [
  { key: "large", value: "Large" },
  { key: "moderate", value: "Moderate" },
  { key: "none", value: "None" },
  { key: "small", value: "Small" },
];

export const YES_NO = [
  { key: "yes", value: "Yes" },
  { key: "no", value: "No" },
];

/** V1: "0-No Pain -> 10-severe". Same scale before and after. */
export const PAIN_SCALE = Array.from({ length: 11 }, (_, n) => ({
  key: String(n),
  value: n === 0 ? "0 — No pain" : n === 10 ? "10 — Severe" : String(n),
}));

/* ── lookup helper ────────────────────────────────────────────────────── */

export const labelFor = (list, key) => (list || []).find((o) => o.key === key)?.value ?? key ?? "";

export const labelsFor = (list, keys) => (Array.isArray(keys) ? keys : []).map((k) => labelFor(list, k));

export const COLOR_STOPS = [
  { pct: 0, rgb: [123, 199, 86] },
  { pct: 0.3, rgb: [205, 220, 57] },
  { pct: 0.5, rgb: [255, 213, 0] },
  { pct: 0.7, rgb: [255, 152, 0] },
  { pct: 1, rgb: [211, 47, 47] },
];

export const getPainColor = (idx, total) => {
  const pct = idx / (total - 1);
  let lower = COLOR_STOPS[0];
  let upper = COLOR_STOPS[COLOR_STOPS.length - 1];
  for (let i = 0; i < COLOR_STOPS.length - 1; i++) {
    if (pct >= COLOR_STOPS[i].pct && pct <= COLOR_STOPS[i + 1].pct) {
      lower = COLOR_STOPS[i];
      upper = COLOR_STOPS[i + 1];
      break;
    }
  }
  const range = upper.pct - lower.pct || 1;
  const t = (pct - lower.pct) / range;
  const rgb = lower.rgb.map((c, i) => Math.round(c + (upper.rgb[i] - c) * t));
  return `rgb(${rgb.join(",")})`;
};

const THUMB_PX = 20;

export const thumbCenter = (idx, maxIdx) => {
  const coeff = maxIdx === 0 ? 0.5 : idx / maxIdx;
  const pxTerm = THUMB_PX * (0.5 - coeff);
  return `calc(${(coeff * 100).toFixed(4)}% + ${pxTerm.toFixed(4)}px)`;
};

export const blockBoundary = (k, maxIdx) => {
  const coeff = (k + 0.5) / maxIdx;
  const pxTerm = THUMB_PX * (0.5 - coeff);
  return `calc(${(coeff * 100).toFixed(4)}% + ${pxTerm.toFixed(4)}px)`;
};
